import { useTranslations } from '@vocab/react';
import {
  Stack,
  Card,
  Toggle,
  Heading,
  Text,
  IconHeart,
  Columns,
  Column,
  TextLink,
} from 'braid-design-system';

import { useLearnMoreLink } from 'src/components/ShowStrongInterestNudge/useLearnMoreLink';
import { Page, PageSection, useTrackingClient } from 'src/context/tracking';

import translations from './DigitalIdentityToggle.vocab';

type DigitalIdentityToggleProps = {
  isShowStrongInterest: boolean;
  setIsShowStrongInterest: (value: boolean) => void;
  hasDigitalIdentityNudge: boolean;
  remainingCount: number;
  nextRenewalDate: string;
  isVerified: boolean;
};

export const DigitalIdentityToggle = ({
  isShowStrongInterest,
  setIsShowStrongInterest,
  hasDigitalIdentityNudge,
  remainingCount,
  nextRenewalDate,
  isVerified,
}: DigitalIdentityToggleProps) => {
  const { t } = useTranslations(translations);
  const client = useTrackingClient();
  const learnMoreLink = useLearnMoreLink();

  return (
    <Card tone="promote" data={{ testid: 'digital-identity-toggle-card' }}>
      <Stack space="large">
        <Heading level="3">{t('Make a strong')}</Heading>
        {hasDigitalIdentityNudge ? (
          <Text>{t('Increase your chance')}</Text>
        ) : (
          <Text>
            {t('Increase your chance verified user', {
              Link: (v) => (
                <TextLink
                  weight="weak"
                  onClick={() => {
                    client.trackLink({
                      link: 'content_element_pressed',
                      eventName: 'content_element_pressed',
                      currentPage: Page.REVIEW_PAGE,
                      currentPageSection: PageSection.REVIEW_APPLICATION,
                      nudgeType: 'show strong interest',
                      linkName: 'learn more',
                      linkAction: 'internal redirect',
                    });
                  }}
                  href={learnMoreLink}
                  data={{
                    automation: 'digital-identity-nudge-learn-more',
                  }}
                  target="_blank"
                >
                  {v}
                </TextLink>
              ),
            })}
          </Text>
        )}
        <Columns space="none">
          <Column width="4/5">
            <Text
              tone="info"
              weight={isShowStrongInterest ? 'strong' : 'regular'}
            >
              <IconHeart active={isShowStrongInterest} />{' '}
              {t('Show strong interest')}
            </Text>
          </Column>
          <Column width="1/5">
            <Toggle
              label=""
              on={isShowStrongInterest}
              onChange={() => {
                client.trackLink({
                  link: 'toggle_pressed',
                  eventName: 'toggle_pressed',
                  toggleState: isShowStrongInterest ? 'off' : 'on',
                  currentPage: Page.REVIEW_PAGE,
                  currentPageSection: PageSection.REVIEW_APPLICATION,
                  verifiedUser: isVerified,
                  toggleType: 'show strong interest',
                });

                setIsShowStrongInterest(!isShowStrongInterest);
              }}
              align="right"
              data={{ testid: 'digital-identity-toggle' }}
            />
          </Column>
        </Columns>
        {hasDigitalIdentityNudge ? (
          <Text tone="secondary" size="small">
            {t('You can still', {
              remainingCount: remainingCount.toString(),
              nextRenewalDate,
            })}
          </Text>
        ) : (
          <>
            {nextRenewalDate ? (
              <Text tone="secondary" size="small">
                {t('You can still verified', {
                  remainingCount: remainingCount.toString(),
                  nextRenewalDate,
                })}
              </Text>
            ) : (
              <Text tone="secondary" size="small">
                {t('You can still without date', {
                  remainingCount: remainingCount.toString(),
                })}
              </Text>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
};
